import React from 'react';
import styled from 'styled-components';

import { Box, Display, Flex, Image, Paragraph, Text } from '@rover/kibble/core';
import type { Props as BoxProps } from '@rover/kibble/core/Box';
import type { Props as DisplayProps } from '@rover/kibble/core/Display';
import type { Props as ParagraphProps } from '@rover/kibble/core/Paragraph';
import { SpacingSystemType } from '@rover/kibble/core/styledSystemPropTypes';
import Accordion, {
  AccordionBodyContent,
  AccordionHeaderClickTarget,
  AccordionHeaderIconWrapper,
} from '@rover/react-lib/src/components/layouts/Accordion';

import messages from './messages.png';

export type Props = BoxProps & {
  imageSrc?: string;
  pb?: SpacingSystemType;
  mt?: SpacingSystemType;
  imageWidth?: number;
  description?: string;
  htmlRef?: React.RefObject<HTMLElement | null>;
  showImage?: boolean;
};

type SubComponents = {
  Header: typeof Display;
  Subheader: typeof Paragraph;
};

const ICON_WRAPPER_WIDTH = 40;
const QUESTION_LINE_HEIGHT_PX = '24px';

const StyledAccordion = styled(Accordion)`
  &:first-of-type {
    border-top: 0 none;
  }

  ${AccordionBodyContent} {
    padding-top: 0px;
    padding-left: ${ICON_WRAPPER_WIDTH}px;
  }

  ${AccordionHeaderClickTarget} {
    // this element doesn't specify left- and right- padding, leaving the browser's default padding for <button>. In order to get a standard width on the icon wrapper, we need to remove this padding deliberately.
    padding-left: 0;
    padding-right: 0;
  }

  ${AccordionHeaderIconWrapper} {
    // TODO - This is a bandaid fix to align the icon horizontally with the question text and vertically with the answer text. We should uplift Accordion eventually and fix this properly.
    flex-basis: ${ICON_WRAPPER_WIDTH}px;
    padding: 0;
    text-align: center;
    line-height: ${QUESTION_LINE_HEIGHT_PX};
  }
`;

/**
 * @deprecated Please use [FaqSection]({@link https://github.com/roverdotcom/web/blob/master/src/frontend/components/src/FaqSection/FaqSection.tsx}) instead.
 *
 * See [HomePageFaqs.tsx]({@link https://github.com/roverdotcom/web/blob/master/src/frontend/react-lib/src/pages/index/HomePage/components/HomePageFaqs.tsx}) for example usage.
 *
 * If the Faq Accordion is needed without the Section container, import `FaqAccordion` from [FaqSection]({@link https://github.com/roverdotcom/web/blob/master/src/frontend/components/src/FaqSection/FaqSection.tsx}) or build custom using [Accordion]({@link https://github.com/roverdotcom/web/blob/master/src/frontend/react-lib/src/components/lists/Accordion/Accordion.tsx})
 */
const FrequentlyAskedQuestions: React.FC<Props> & SubComponents = ({
  imageSrc = messages,
  imageWidth = 242,
  pb = ['16x', '24x'],
  mt = ['16x', '24x'],
  children,
  description,
  htmlRef,
  showImage = true,
  ...boxProps
}: Props) => (
  <Box
    webProps={{ itemScope: true, itemType: 'https://schema.org/FAQPage' }}
    mt={mt}
    pb={pb}
    {...boxProps}
    ref={htmlRef}
  >
    <Flex flexWrap={['wrap', 'nowrap']} gap="16x" justifyContent="center">
      <Box flex="2 1 80%">{children}</Box>
      {showImage && (
        <Box
          flexBasis={`${imageWidth}px`}
          flexShrink={['1', '1', '0']} // don't allow the image to shrink on desktop
          textAlign="center"
          maxWidth={`${imageWidth}px`}
          display={['none', 'block']}
        >
          <Image src={imageSrc} description={description || ''} height="auto" width="100%" />
        </Box>
      )}
    </Flex>
  </Box>
);

FrequentlyAskedQuestions.Header = ({ children, ...displayProps }: DisplayProps) => (
  <Display size="500" mb="4x" {...displayProps}>
    {children}
  </Display>
);

FrequentlyAskedQuestions.Subheader = ({ children, ...paragraphProps }: ParagraphProps) => (
  <Paragraph webProps={{ maxWidth: 'none' }} size="200" marginBottom="6x" {...paragraphProps}>
    {children}
  </Paragraph>
);

const Question: React.FC<{ children: string }> = ({ children }) => (
  <Text fontWeight="semibold" textColor="primary" size="300" itemProp="name">
    {children}
  </Text>
);

const Answer: React.FC<{ children: React.ReactElement }> = ({ children }) => (
  <Box
    webProps={{
      itemScope: true,
      itemProp: 'acceptedAnswer',
      itemType: 'https://schema.org/Answer',
    }}
  >
    <Box webProps={{ itemProp: 'text' }}>{children}</Box>
  </Box>
);

export const FaqParagraph: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Paragraph webProps={{ maxWidth: 'none' }} sx={{ '&:last-child': { marginBottom: '0' } }}>
    {children}
  </Paragraph>
);

export type FrequentlyAskedQuestionProps = {
  question: string;
  children: React.ReactElement;
} & Omit<
  React.ComponentProps<typeof Accordion>,
  'header' | 'body' | 'isStackedAccordion' | 'getIcon' | 'iconLeft'
>;

export const FrequentlyAskedQuestion: React.FC<FrequentlyAskedQuestionProps> = ({
  question,
  children: answer,
  onToggle,
  ...props
}) => (
  <StyledAccordion
    header={<Question>{question}</Question>}
    body={<Answer>{answer}</Answer>}
    isStackedAccordion={false}
    getIcon={(isOpen) => (isOpen ? 'rover-icon-in-up' : 'rover-icon-in-down')}
    onToggle={onToggle}
    {...props}
    itemScope
    itemProp="mainEntity"
    itemType="https://schema.org/Question"
  />
);

export default FrequentlyAskedQuestions;
